import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      inline-flex items-center justify-center
      font-semibold font-body
      rounded-[var(--radius-kid)]
      transition-all duration-200
      focus:outline-none focus:ring-4
      disabled:opacity-50 disabled:cursor-not-allowed
    `

    const variantStyles = {
      primary: `
        bg-[var(--color-kids-primary)] text-white
        hover:bg-[var(--color-kids-primary-dark)]
        focus:ring-[var(--color-kids-primary)]/30
        shadow-[var(--shadow-kid)]
        hover:shadow-[var(--shadow-kid-hover)]
      `,
      secondary: `
        bg-[var(--color-kids-secondary)] text-white
        hover:bg-[var(--color-kids-secondary-dark)]
        focus:ring-[var(--color-kids-secondary)]/30
        shadow-[var(--shadow-kid)]
        hover:shadow-[var(--shadow-kid-hover)]
      `,
      ghost: `
        bg-transparent text-[var(--color-kids-text)]
        hover:bg-[var(--color-kids-surface-alt)]
        focus:ring-[var(--color-kids-primary)]/20
      `,
      danger: `
        bg-[var(--color-kids-danger)] text-white
        hover:bg-[var(--color-kids-danger-dark)]
        focus:ring-[var(--color-kids-danger)]/30
      `,
      success: `
        bg-[var(--color-kids-success)] text-white
        hover:bg-[var(--color-kids-success-dark)]
        focus:ring-[var(--color-kids-success)]/30
      `,
    }

    // All sizes ensure minimum 44px height for WCAG 2.1 AAA tap target compliance
    const sizeStyles = {
      sm: 'px-4 py-2.5 min-h-[44px] text-[var(--font-size-kid-sm)]',
      md: 'px-6 py-3 min-h-[44px] text-[var(--font-size-kid-base)]',
      lg: 'px-8 py-4 min-h-[48px] text-[var(--font-size-kid-lg)]',
    }

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variantStyles[variant], sizeStyles[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
